// BaseRosterContext.js

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { useUser } from 'context/UserContext';
import { useBase } from 'context/BaseContext';
import { getBaseKey, subscribeToBases } from 'services/bases/BaseReadService';

const BaseRosterContext = createContext(null);

export const useBaseRoster = () => {
  const context = useContext(BaseRosterContext);

  if (!context) {
    throw new Error('useBaseRoster must be used within a BaseRosterProvider');
  }

  return context;
};

/**
 * Keeps the user's base roster live for the entire authenticated session.
 */
export const BaseRosterProvider = ({ children }) => {
  const { user } = useUser();
  const { activeBase } = useBase();
  const [bases, setBases] = useState([]);
  const [hasLoaded, setHasLoaded] = useState(false);
  // Optimistically hide bases mid-delete so the UI can switch away immediately.
  const [hiddenBaseKeys, setHiddenBaseKeys] = useState(() => new Set());

  useEffect(() => {
    if (!user?.key) {
      setBases([]);
      setHasLoaded(false);
      setHiddenBaseKeys(new Set());
      return undefined;
    }

    setHasLoaded(false);
    const unsubscribe = subscribeToBases(user.key, (rows) => {
      setBases(Array.isArray(rows) ? rows : []);
      setHasLoaded(true);
    });

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [user?.key]);

  // Drop hide flags once the live roster no longer includes those keys.
  useEffect(() => {
    setHiddenBaseKeys((previous) => {
      if (!previous.size) return previous;
      const liveKeys = new Set(
        (Array.isArray(bases) ? bases : [])
          .map((row) => getBaseKey(row))
          .filter(Boolean),
      );
      let changed = false;
      const next = new Set();
      previous.forEach((key) => {
        if (liveKeys.has(key)) {
          next.add(key);
        } else {
          changed = true;
        }
      });
      return changed ? next : previous;
    });
  }, [bases]);

  const hideBase = useCallback((baseKey) => {
    const key = String(baseKey || '').trim();
    if (!key) return;
    setHiddenBaseKeys((previous) => {
      if (previous.has(key)) return previous;
      const next = new Set(previous);
      next.add(key);
      return next;
    });
  }, []);

  const unhideBase = useCallback((baseKey) => {
    const key = String(baseKey || '').trim();
    if (!key) return;
    setHiddenBaseKeys((previous) => {
      if (!previous.has(key)) return previous;
      const next = new Set(previous);
      next.delete(key);
      return next;
    });
  }, []);

  const mergedBases = useMemo(() => {
    const activeKey = String(activeBase?.key || activeBase?.id || '').trim();
    const rows = Array.isArray(bases) ? bases : [];

    const merged = !activeKey
      ? rows
      : rows.map((row) => {
        const rowKey = String(row?.key || row?.id || '').trim();
        return rowKey === activeKey ? { ...row, ...activeBase } : row;
      });

    if (!hiddenBaseKeys.size) return merged;
    return merged.filter((row) => !hiddenBaseKeys.has(getBaseKey(row)));
  }, [activeBase, bases, hiddenBaseKeys]);

  const value = useMemo(() => ({
    bases: mergedBases,
    hasLoaded,
    hideBase,
    unhideBase,
  }), [hasLoaded, hideBase, mergedBases, unhideBase]);

  return (
    <BaseRosterContext.Provider value={value}>
      {children}
    </BaseRosterContext.Provider>
  );
};

export default BaseRosterContext;
